import React, { useContext, useRef, useState } from 'react';
import {
  Button,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  Switch,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  Tooltip,
} from '@patternfly/react-core';
import { InfoCircleIcon, TimesIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { storesContext } from 'stores';
import { LabCardRow } from '../../LabCard';
import type LabEnvironmentStore from '../../store';
import DynamicLaunchParameterStore from './store';
import './styles.scss';

type FilterOption = {
  label: string;
  value: string;
};

type FilterValue = {
  value?: FilterOption;
};

type DynamicLaunchParameterProps = {
  labStore: LabEnvironmentStore;
};

type RegionSelectProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selected: string;
  selectedLabel: string;
  options: FilterOption[];
  onSelect: (
    event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => void;
};

const RegionSelect = ({
  isOpen,
  onOpenChange,
  selected,
  selectedLabel,
  options,
  onSelect,
}: RegionSelectProps) => {
  const toggleRef = useRef<HTMLButtonElement>(null);

  return (
    <Select
      id="dynamic-launch-region-select"
      isOpen={isOpen}
      selected={selected}
      onSelect={onSelect}
      onOpenChange={onOpenChange}
      toggle={{
        toggleRef,
        toggleNode: (
          <MenuToggle
            ref={toggleRef}
            onClick={() => onOpenChange(!isOpen)}
            isExpanded={isOpen}
            className="region-single"
            data-testid="dynamic-launch-region-toggle"
          >
            {selectedLabel}
          </MenuToggle>
        ),
      }}
    >
      <SelectList>
        {options.map((option) => (
          <SelectOption key={option.value} value={option.value}>
            {option.label}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
};

type HeatTemplateSelectProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selected: string;
  filterValue: string;
  onFilterChange: (
    event: React.FormEvent<HTMLInputElement>,
    value: string,
  ) => void;
  onSelect: (
    event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => void;
  onClear: () => void;
  options: FilterOption[];
  placeholder: string;
  ariaLabel: string;
  clearLabel: string;
  emptyLabel: string;
};

const HeatTemplateSelect = ({
  isOpen,
  onOpenChange,
  selected,
  filterValue,
  onFilterChange,
  onSelect,
  onClear,
  options,
  placeholder,
  ariaLabel,
  clearLabel,
  emptyLabel,
}: HeatTemplateSelectProps) => {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Select
      id="dynamic-launch-heat-select"
      isOpen={isOpen}
      selected={selected}
      onSelect={onSelect}
      onOpenChange={onOpenChange}
      variant="typeahead"
      toggle={{
        toggleRef,
        toggleNode: (
          <MenuToggle
            ref={toggleRef}
            variant="typeahead"
            onClick={() => onOpenChange(!isOpen)}
            isExpanded={isOpen}
            className="heat-template-single"
            data-testid="dynamic-launch-heat-toggle"
          >
            <TextInputGroup isPlain>
              <TextInputGroupMain
                value={isOpen ? filterValue : filterValue || selected}
                onChange={onFilterChange}
                onFocus={() => {
                  if (!isOpen) {
                    onOpenChange(true);
                  }
                }}
                innerRef={inputRef}
                placeholder={placeholder}
                autoComplete="off"
                aria-label={ariaLabel}
              />
              {(filterValue || selected) && (
                <TextInputGroupUtilities>
                  <Button
                    variant="plain"
                    onClick={() => {
                      onClear();
                      inputRef.current?.focus();
                    }}
                    aria-label={clearLabel}
                  >
                    <TimesIcon />
                  </Button>
                </TextInputGroupUtilities>
              )}
            </TextInputGroup>
          </MenuToggle>
        ),
      }}
    >
      <SelectList className="dynamic-launch__heat-options">
        {options.length > 0 ? (
          options.map((option) => (
            <SelectOption key={option.value} value={option.value}>
              {option.label}
            </SelectOption>
          ))
        ) : (
          <SelectOption isDisabled>{emptyLabel}</SelectOption>
        )}
      </SelectList>
    </Select>
  );
};

/**
 * Power-user Dynamic Launch Parameters restored into the LabCardRow pattern.
 * Preserves region, disconnected internet, and OSP heat template controls
 * that were dropped during the Panel → LabCardRow migration.
 */
const DynamicLaunchParameter: React.FC<DynamicLaunchParameterProps> = ({
  labStore,
}) => {
  const { t } = useTranslation();
  const { userStore } = useContext(storesContext);
  const store = useLocalStore(() => new DynamicLaunchParameterStore());
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const [isHeatOpen, setIsHeatOpen] = useState(false);
  const [heatFilterValue, setHeatFilterValue] = useState('');

  if (!userStore.isLabPowerUser) {
    return null;
  }

  const panelId = 'lab-dynamic-launch';
  const isExpanded = store.expandedPanels?.has(panelId);

  const regionOptions: FilterOption[] = labStore.regionsFilterOptions();
  const regionsFilterValue = labStore.regionsFilterValue as FilterValue;
  const selectedRegion =
    regionsFilterValue?.value?.value ?? labStore.powerUserRegion;

  const selectedRegionLabel =
    regionOptions.find((option) => option.value === selectedRegion)?.label ||
    selectedRegion ||
    t('Select Region');

  const heatOptions: FilterOption[] = labStore.heatTemplatesOptions;
  const heatTemplateFilterValue =
    labStore.heatTemplateFilterValue as FilterValue;
  const selectedHeatTemplate =
    heatTemplateFilterValue?.value?.value ?? labStore.powerUserHeatTemplate;

  const filteredHeatOptions = heatFilterValue
    ? heatOptions.filter((option) =>
        option.label.toLowerCase().includes(heatFilterValue.toLowerCase()),
      )
    : heatOptions;

  const onRegionSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    labStore.setPowerUserRegion(value ? String(value) : '');
    setIsRegionOpen(false);
  };

  const onHeatSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    labStore.setPowerUserHeatTemplate(value ? String(value) : '');
    setHeatFilterValue('');
    setIsHeatOpen(false);
  };

  const onHeatFilterChange = (
    _event: React.FormEvent<HTMLInputElement>,
    value: string,
  ) => {
    setHeatFilterValue(value);
    if (!isHeatOpen) {
      setIsHeatOpen(true);
    }
  };

  const clearHeatSelection = () => {
    setHeatFilterValue('');
    labStore.setPowerUserHeatTemplate('');
  };

  const onHeatOpenChange = (open: boolean) => {
    setIsHeatOpen(open);
    if (!open) {
      setHeatFilterValue('');
    }
  };

  const heatTemplateTooltip = t(
    'Start typing to search and narrow down your choices.',
  );

  return (
    <LabCardRow
      label={t('Dynamic Launch Parameters')}
      isExpanded={Boolean(isExpanded)}
      onToggle={() => store.togglePanels(panelId, !isExpanded)}
      analyticsId="dynamic-launch-vt-dash-lp"
    >
      <div
        className="dynamic-launch-widget"
        data-testid="dynamic-launch-widget"
      >
        <ul className="dynamic-launch">
          <li className="dynamic-launch__field">
            <span className="dynamic-launch__label">{t('Select Region:')}</span>
            <RegionSelect
              isOpen={isRegionOpen}
              onOpenChange={setIsRegionOpen}
              selected={selectedRegion}
              selectedLabel={selectedRegionLabel}
              options={regionOptions}
              onSelect={onRegionSelect}
            />
          </li>

          <li className="dynamic-launch__field">
            <Switch
              className="disconnected-switch"
              label={t('Disconnected')}
              isDisabled={labStore.internetAccessSwitchDisable}
              isChecked={labStore.internetAccessDisconnected}
              hasCheckIcon
              data-testid="dynamic-launch-disconnected-switch"
              onChange={(_event, checked) => {
                if (!labStore.internetAccessCommandRequested) {
                  labStore.setInternetAccessDisconnected(checked);
                  labStore.doCommand('internet_access', { access: !checked });
                }
              }}
            />
          </li>

          <li className="dynamic-launch__field">
            <span className="dynamic-launch__label">
              {t('Select OSP heat template')}:
              <Tooltip content={heatTemplateTooltip}>
                <span
                  className="with-info-icon"
                  aria-label={heatTemplateTooltip}
                >
                  <InfoCircleIcon
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  />
                </span>
              </Tooltip>
            </span>
            <HeatTemplateSelect
              isOpen={isHeatOpen}
              onOpenChange={onHeatOpenChange}
              selected={selectedHeatTemplate}
              filterValue={heatFilterValue}
              onFilterChange={onHeatFilterChange}
              onSelect={onHeatSelect}
              onClear={clearHeatSelection}
              options={filteredHeatOptions}
              placeholder={t('Type to search')}
              ariaLabel={t('Select OSP heat template')}
              clearLabel={t('Clear')}
              emptyLabel={t('No heat templates found')}
            />
          </li>
        </ul>
      </div>
    </LabCardRow>
  );
};

export default observer(DynamicLaunchParameter);
